import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from 'shared/components/ui/use-toast';

import { ChatScrollContext } from './hooks/useChatScrollContext';
import { useChatScrollToBottom, useConstraintsMetadata, useHandleAttachments } from './hooks';
import { useConstraintsChangeDetection } from './hooks/useConstraintsChangeDetection';
import { useConstraintsSync } from './hooks/useConstraintsSync';

import { useStreetBeatChatLegacy as useStreetBeatChat } from 'shared/sdk-compat/useStreetBeatChatLegacy';
import { useConversationFlow, useChatErrors, useTasks, useThreadRouteSync } from '@dev-streetbeat/chat/react';

import usePrevious from 'shared/hooks/usePrevious';
import chatStore from 'shared/store/chatStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import constraintsStore from 'shared/store/constraintsStore';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import { buildConstraintsSyncPayload } from 'pages/assistant/components/activity-panel/panels/constraints/lifecycle/buildConstraintsSyncPayload';
import { CONSTRAINTS } from 'pages/assistant/components/activity-panel/panels/constraints/constants';

import { MessagesList } from './MessagesList';
import { ChatWelcome } from './components/ChatWelcome';
import { GuidedPortfoliosDialog } from 'pages/assistant/components/guided-portfolios/GuidedPortfoliosDialog';
import { PortfolioDocumentImport } from './components/portfolio-import/PortfolioDocumentImport';
import { Dialog, DialogContent } from 'shared/components/ui/dialog';
import { useDocUploadAvailable } from './components/portfolio-import/useDocUploadAvailable';
import { ManualTableSeed } from 'shared/store/portfolioDocImportStore';
import { buildManualPortfolioPayload } from './components/manualPortfolioPayload';
import { ChatInput, MessageData } from './components/ChatInput';
import { ChatGradient } from './components/ChatGradient';
import { ScrollArea, ScrollAreaRef } from 'shared/components/ui/scroll-area';
import { deriveTaskUpdate, isChatInputLocked } from './utils/deriveTaskUpdate';

import styles from './chatPanel.module.scss';
import { useTranslation } from 'react-i18next';
import { ChatLoading } from './components/ChatLoading';

// The composer paperclip's message: the agent analyses the confirmed rows it
// finds in the message metadata (DOCUMENT_PORTFOLIO_KEY), with no form. The
// starter «Carica un portafoglio manualmente, analizza e genera report» still
// opens the manual form.
export const DOCUMENT_PORTFOLIO_PROMPT = 'Analizza il portafoglio caricato dal documento';
export const DOCUMENT_PORTFOLIO_KEY = 'fineco:fineco_document_portfolio';

export const ChatPanel: React.FC = () => {
  const {
    messages,
    pendingAction,
    createThreadAndStart,
    reconnectToThread,
    continueThread,
    cancel,
    threadId,
    streamingMessage,
    isConnectingToThread,
  } = useStreetBeatChat();
  const { isLoading: runInProgress } = useConversationFlow();
  const { timeoutError, hasTimeoutError, clearTimeoutError, hasError, clearError } = useChatErrors();
  const { tasks, getLatestTask } = useTasks();
  const navigate = useNavigate();
  const params = useParams();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Global chat state
  const {
    currentThreadId: globalThreadId,
    setCurrentThreadId,
    resetChatState,
    pendingInitialMessage,
    setPendingInitialMessage,
  } = chatStore();

  // Catalog-loaded flag — `useSessionConstraints` flips this once profiles +
  // predefiniti are prefetched. The SSO-import consumer below waits on this
  // so it can resolve the user's default profile (if any) for the imported
  // mode and bake its reference into the initial thread metadata.
  const sessionLoaded = constraintProfilesStore((s) => s.loaded);

  // Handle file attachments
  useHandleAttachments();

  // Handle constraints metadata
  useConstraintsMetadata();

  // Handle constraints change detection for red dots and popups
  useConstraintsChangeDetection();

  // Flush pendingSync (applied profile / predefiniti / Reset) to thread metadata
  useConstraintsSync();

  const [showArrow, setShowArrow] = useState(false);
  const [docImportOpen, setDocImportOpen] = useState(false);
  // Builder mode only (D19 Q3): the manual-portfolio tool is builder-scoped.
  const docUploadAvailable = useDocUploadAvailable();
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [threadCreationKickstarted, setThreadCreationKickstarted] = useState(false);

  const currentThreadId = useMemo(() => params.threadId, [params.threadId]);
  const previousThreadId = usePrevious(currentThreadId);

  const previousPendingAction = usePrevious(pendingAction);
  const previousMessages = usePrevious(messages);

  // Get the latest active task for display
  const latestActiveTask = getLatestTask();

  const isThreadInitialized = !!currentThreadId;

  const showWelcomeContainer = !isThreadInitialized && !threadCreationKickstarted && !pendingInitialMessage;
  // The SDK's createThreadAndStart resolves once the first message is on the
  // socket; the thread id arrives later (gateway v2: after the filters and the
  // turn's admission, up to seconds). Keep "Connessione in corso" until then,
  // not the starters or a blank panel.
  const awaitingThread = threadCreationKickstarted && !isThreadInitialized;
  const showLoadingContainer = isConnectingToThread || isCreatingThread || awaitingThread;
  const showMessagesContainer = !isConnectingToThread && !isCreatingThread && isThreadInitialized;
  const showInput = !isConnectingToThread || !isThreadInitialized;

  const scrollAreaRef = useRef<ScrollAreaRef>(null);

  const showError = useCallback(
    (errorMessage: string) => {
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      if (hasError) {
        clearError();
      }
    },
    [hasError, clearError],
  );

  // A send the backend refused before creating the thread (busy, rate limit):
  // back to the welcome screen instead of "Connessione in corso" for good.
  useEffect(() => {
    if (hasError && !isThreadInitialized) setThreadCreationKickstarted(false);
  }, [hasError, isThreadInitialized]);

  useEffect(() => {
    if (hasTimeoutError) {
      showError(timeoutError);
      clearTimeoutError();
    }
  }, [timeoutError]);

  // `extraMetadata` rides on the message itself: ACE writes it to the thread
  // metadata before it adds the message and starts the run, on a new thread
  // and on an existing one alike.
  const handleSendMessage = useCallback(
    async (data: MessageData, extraMetadata?: Record<string, unknown>): Promise<boolean> => {
      if (!data.text?.trim()) {
        return false; // Don't send empty messages
      }

      let errorOccurred = false;

      try {
        if (!currentThreadId) {
          setThreadCreationKickstarted(true);
          setIsCreatingThread(true);
          // Build the full constraints payload via the shared helper so the
          // first message carries BOTH the working document AND the applied
          // profile reference — the previous direct construction omitted
          // APPLIED_PROFILE_KEY, which made the thread reset to "Predefiniti
          // (modificato)" on creation even when MANY was the active default.
          const mode = activityPanelStore.getState().constraintsPanelMode as
            | 'builder'
            | 'rebalancing';
          const { metadata } = buildConstraintsSyncPayload({ includeMode: mode });
          // Mark the sync in flight so the panel actions stay gated until
          // the createThreadAndStart stream completes.
          constraintsStore.getState().setSyncInFlight(true);
          // Clear pendingSync BEFORE awaiting the SDK: URL navigation to the
          // newly-created thread happens mid-call, which re-runs
          // useConstraintsSync with threadId set + pendingSync still set,
          // firing a redundant seedThread for the same content we're sending
          // via createThreadAndStart.
          constraintsStore.getState().clearPendingSync();
          try {
            await createThreadAndStart(data.text, { ...metadata, ...extraMetadata });
          } finally {
            constraintsStore.getState().setSyncInFlight(false);
          }
        } else {
          await continueThread(currentThreadId, data.text, extraMetadata);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        showError(error.message);
        errorOccurred = true;
        if (!currentThreadId) setThreadCreationKickstarted(false);
      } finally {
        setIsCreatingThread(false);
      }

      return !errorOccurred;
    },
    [currentThreadId, createThreadAndStart, continueThread, showError],
  );

  useEffect(() => {
    // "!threadId" is important to avoid resetting the component state when we are navigating after thread
    if (currentThreadId !== previousThreadId && previousThreadId) {
      resetChatState(); // Reset global chat state when switching threads
      setShowArrow(false);
      setIsCreatingThread(false);
      // The anti-flash flag set by the first message of a thread must not
      // outlive that thread: ChatPanel's instance survives the
      // thread → /assistant transition (ChatPanelWithProvider keeps the same
      // provider key on "abc" → undefined), so without this reset the
      // welcome screen stays suppressed on every later "Nuova chat".
      setThreadCreationKickstarted(false);
    }
  }, [currentThreadId]);

  useEffect(() => {
    const connectToThread = async () => {
      try {
        await reconnectToThread(currentThreadId);
      } catch (error) {
        console.error('Failed to reconnect to thread:', error);
        const isSignalAbortError = error.message.includes('signal is aborted without reason');
        const isWsClosedError = error.message === 'WebSocket connection error';
        if (!isSignalAbortError && !isWsClosedError) {
          showError(error.message);
        }
      }
    };

    if (currentThreadId) {
      connectToThread();
    }
  }, [currentThreadId]);

  // Sync global thread ID with URL and SDK thread ID
  useEffect(() => {
    if (threadId !== globalThreadId) {
      setCurrentThreadId(threadId);
    }
  }, [globalThreadId, threadId]);

  // v16 routes thread creation through useThreadRouteSync: in uncontrolled-mode
  // mounts the runtime owns thread lifecycle, and lastCreatedThreadId is only
  // observable via this hook. When the runtime creates a new thread (e.g. via
  // createThreadAndStart from the JWT-driven pendingInitialMessage path) and
  // the URL has not yet adopted any thread, this fires onThreadChange with the
  // new thread id — we navigate the URL there.
  useThreadRouteSync({
    selectedThreadId: currentThreadId ?? null,
    onThreadChange: (newThreadId) => {
      if (newThreadId) {
        navigate(`/assistant/${newThreadId}`);
      }
    },
  });

  // Handle auto-sending pendingInitialMessage for rebalancing.
  //
  // JwtAuthService stashes `pendingInitialMessage` with metadata that carries
  // ONLY `fineco:fineco_imported_portfolio` + `CONSTRAINTS.MODE_KEY`. Before
  // this effect, the metadata sent to `createThreadAndStart` did not include
  // the user's default profile reference, so the thread was created with an
  // empty `APPLIED_PROFILE_KEY` and the Vincoli dropdown landed on
  // "Predefiniti" even when a default rebalancing profile existed.
  //
  // We now mirror what `useFreshChatBootstrap` + `handleSendMessage` do on
  // the `/assistant` route: resolve the per-mode default profile, apply it
  // locally, build the constraints payload from the store, and merge it
  // into the JWT-stashed metadata. Gated on `sessionLoaded` so the profile
  // catalog is guaranteed populated when we look up the default.
  useEffect(() => {
    if (!pendingInitialMessage || currentThreadId || isCreatingThread || isConnectingToThread) return;
    if (!sessionLoaded) return;

    console.info('ChatPanel: Auto-sending pendingInitialMessage:', pendingInitialMessage);

    const sendPendingMessage = async () => {
      try {
        // Same anti-flash flag as a typed first message: no starters while the
        // thread is being created.
        setThreadCreationKickstarted(true);
        setIsCreatingThread(true);

        // Resolve mode from the JWT-stashed metadata and apply the per-mode
        // default profile (or fall back to predefiniti) to the local store
        // BEFORE constructing the payload. `applyProfile` / `applyPredefiniti`
        // also set workingDocument so `buildConstraintsSyncPayload` reads
        // the right state.
        const mode = (pendingInitialMessage.metadata?.[CONSTRAINTS.MODE_KEY] as { mode?: string } | undefined)?.mode;
        const isConstraintsMode = mode === 'builder' || mode === 'rebalancing';
        if (isConstraintsMode) {
          const defaultProfile = constraintProfilesStore
            .getState()
            .profilesByMode[mode]
            .find((p) => p.is_default);
          if (defaultProfile) {
            constraintsStore.getState().applyProfile(defaultProfile);
          } else {
            constraintsStore.getState().applyPredefiniti();
          }
        }

        // Build the constraints payload from the freshly-applied store and
        // merge with the JWT-stashed metadata. JWT keys first, constraints
        // keys second — constraints' MODE_KEY equals the JWT's so the merge
        // is idempotent on that field while adding METADATA_KEY +
        // APPLIED_PROFILE_KEY.
        const { metadata: constraintsMeta } = isConstraintsMode
          ? buildConstraintsSyncPayload({ includeMode: mode })
          : { metadata: {} };
        const mergedMetadata = { ...pendingInitialMessage.metadata, ...constraintsMeta };

        // Match `handleSendMessage`'s first-message branch: gate the panel
        // for the SDK promise lifetime, and drop pendingSync so the later
        // re-render after URL navigation doesn't fire a duplicate seedThread.
        constraintsStore.getState().setSyncInFlight(true);
        constraintsStore.getState().clearPendingSync();
        try {
          await createThreadAndStart(pendingInitialMessage.text, mergedMetadata);
        } finally {
          constraintsStore.getState().setSyncInFlight(false);
        }
        setPendingInitialMessage(null);
      } catch (error) {
        console.error('Failed to send pending initial message:', error);
        showError(error.message);
        setThreadCreationKickstarted(false);
      } finally {
        setIsCreatingThread(false);
      }
    };

    sendPendingMessage();
  }, [
    pendingInitialMessage,
    currentThreadId,
    isCreatingThread,
    isConnectingToThread,
    sessionLoaded,
    createThreadAndStart,
    setPendingInitialMessage,
    showError,
  ]);

  // Track if we've initialized mode for current chat session
  const modeInitializedRef = useRef(false);

  // Reset mode initialization flag when thread changes
  useEffect(() => {
    if (previousThreadId !== currentThreadId) {
      modeInitializedRef.current = false;
    }
  }, [currentThreadId, previousThreadId]);

  // Set constraints panel mode based on pending message or new chat
  useEffect(() => {
    // Only run when not in a thread (new chat scenario) and not yet initialized
    if (!currentThreadId && !modeInitializedRef.current) {
      modeInitializedRef.current = true; // Set flag immediately to prevent duplicate runs

      if (pendingInitialMessage) {
        // Check if pending message has feature metadata
        const finecoMetadata = pendingInitialMessage.metadata?.['fineco:fineco_imported_portfolio'];
        // Use feature field if available, otherwise fall back to builder
        const feature = finecoMetadata?.feature as 'rebalancing' | 'diagnosis' | 'builder' | undefined;
        const mode = feature || 'builder';

        // Only switch the legacy store if different from current mode
        const currentMode = activityPanelStore.getState().constraintsPanelMode;
        if (currentMode !== mode) {
          activityPanelStore.getState().setConstraintsPanelMode(mode);
        }
        // Set the unified store's mode unconditionally — it must not depend on
        // whether the legacy store's value happened to change.
        if (mode === 'builder' || mode === 'rebalancing') {
          constraintsStore.getState().setMode(mode);
        }
      } else {
        // No pending message - default to builder mode
        const currentMode = activityPanelStore.getState().constraintsPanelMode;
        if (currentMode !== 'builder') {
          activityPanelStore.getState().setConstraintsPanelMode('builder');
        }
        constraintsStore.getState().setMode('builder');
      }
    }
  }, [pendingInitialMessage, currentThreadId]);

  // The composer paperclip: the confirmed rows go as message metadata (never
  // in the text) and the agent analyses them directly — no manual form, no
  // second confirmation.
  const handleDocImportUse = useCallback(
    (seed: ManualTableSeed) => {
      setDocImportOpen(false);
      void handleSendMessage(
        { text: DOCUMENT_PORTFOLIO_PROMPT },
        { [DOCUMENT_PORTFOLIO_KEY]: buildManualPortfolioPayload(seed.rows, seed.byWeights) },
      );
    },
    [handleSendMessage],
  );

  const { scrollToBottom, scrollToLastAgentMessage, messagesEndRef, lastAgentMessageRef } = useChatScrollToBottom();

  const showScrollToBottomButton = (_event: React.UIEvent<HTMLDivElement>, isAtBottom: boolean) => {
    setShowArrow(!isAtBottom);
  };

  const skipScrollToBottomRef = useRef(false);
  useEffect(() => {
    if (showMessagesContainer) {
      skipScrollToBottomRef.current = true;
      scrollToLastAgentMessage();
    }
  }, [showMessagesContainer, scrollToLastAgentMessage]);

  // Armed by sends from inside the message list (e.g. clicking a fund in the
  // Wrapper list, issue #89): the user is typically scrolled up, so `showArrow`
  // would suppress the autoscroll below and the new message would land out of
  // view. Scrolling at send time does not work either — the smooth scroll
  // targets the bottom as it is BEFORE the message is committed and stops
  // short once the list grows. So we scroll when the commit actually lands.
  const forceScrollOnNextMessageRef = useRef(false);
  const scrollToBottomOnNextMessage = useCallback(() => {
    forceScrollOnNextMessageRef.current = true;
  }, []);

  // Handle message updates and smart scrolling
  useEffect(() => {
    const isMessagesChanged = previousMessages !== messages && messages.length > 0;
    if (forceScrollOnNextMessageRef.current && isMessagesChanged) {
      forceScrollOnNextMessageRef.current = false;
      skipScrollToBottomRef.current = false;
      scrollToBottom(true);
      return;
    }
    if (skipScrollToBottomRef.current) {
      skipScrollToBottomRef.current = false;
      return;
    }
    if (showArrow) return;
    const isPendingActionChanged = previousPendingAction !== pendingAction && !!pendingAction;

    const smoothScroll = isPendingActionChanged || isMessagesChanged;

    scrollToBottom(smoothScroll);
  }, [tasks, messages, pendingAction, streamingMessage, showArrow, scrollToBottom]);

  const taskUpdate = useMemo(
    () => deriveTaskUpdate({ runInProgress, pendingAction, latestActiveTask, messages, t }),
    [latestActiveTask, runInProgress, t, pendingAction, messages],
  );

  const scrollContextValue = useMemo(
    () => ({
      scrollToBottom,
      scrollToBottomOnNextMessage,
      messagesEndRef,
      lastAgentMessageRef,
    }),
    [scrollToBottom, scrollToBottomOnNextMessage],
  );

  return (
    <ChatScrollContext.Provider value={scrollContextValue}>
      <div className={styles.container}>
        {/* Welcome screen when no messages or no threadId */}
        {showWelcomeContainer && <ChatWelcome sendUserMessage={handleSendMessage} />}

        <GuidedPortfoliosDialog
          threadId={currentThreadId}
          onImport={(text) => {
            void handleSendMessage({ text });
          }}
        />

        <Dialog open={docImportOpen} onOpenChange={setDocImportOpen}>
          <DialogContent title="Importa da documento" className="max-w-[1100px]" data-testid="doc-upload-dialog">
            <PortfolioDocumentImport onUse={handleDocImportUse} />
          </DialogContent>
        </Dialog>

        {showLoadingContainer && <ChatLoading />}

        {/* Messages list */}
        {showMessagesContainer && (
          <>
            <ChatGradient
              direction="to bottom"
              classNameInner="-mb-10 h-12"
              classNameOuter="pointer-events-none z-[9] !-mb-2"
            />

            <ScrollArea
              ref={scrollAreaRef}
              onScroll={showScrollToBottomButton}
              className={styles.scrollArea}
              contentClassName={styles.scrollAreaContent}
            >
              <div className={styles.messagesList}>
                <MessagesList />
              </div>

              <div className={styles.messagesSpacer} />
              <div ref={messagesEndRef} />
            </ScrollArea>
          </>
        )}

        {/* Chat input for when we have messages */}
        {showInput && (
          <div className={styles.chatInputContainer}>
            <ChatInput
              onSend={handleSendMessage}
              onCancel={cancel}
              taskUpdate={taskUpdate}
              disabled={isChatInputLocked(runInProgress, pendingAction)}
              showArrow={showArrow}
              arrowAction={() => scrollToBottom(true)}
              onAttach={docUploadAvailable ? () => setDocImportOpen(true) : undefined}
            />
          </div>
        )}

        {/* Bottom spacer for consistent padding */}
        <div className={styles.bottomSpacer} />
      </div>
    </ChatScrollContext.Provider>
  );
};
